import { useEffect, useState, useCallback, Component } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/common/TicketPopup.css";
import { useTicketSocket } from "../context/TicketSocketContext";

// ⚙️ تنظیمات وب‌ساکت — اتصال واقعی داخل TicketSocketContext برقرار می‌شه،
// این کامپوننت فقط از اونجا پیام می‌خونه. این مقدار رو با ایونت واقعی بک‌اندت جایگزین کن
const NEW_TICKET_EVENT = "new_ticket"; // نوع پیامی که سرور برای تیکت جدید می‌فرسته

// هر پیام دریافتی از سرور انتظار می‌ره این شکلی باشه (در صورت نیاز خودت وفق بده):
// { type: "new_ticket", ticket: { id, subject, sender_name, created_at } }

const AUTO_DISMISS_MS = 7000; // بعد از چند میلی‌ثانیه پاپ‌اپ خودش بسته بشه
const MAX_VISIBLE = 4; // حداکثر تعداد پاپ‌اپ هم‌زمان روی هم

let idCounter = 0;
const nextId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `ticket-toast-${++idCounter}`;

// پیام‌های وب‌ساکت همیشه شکل مورد انتظار رو ندارن (مثلاً subject می‌تونه یه آبجکت
// یا عدد باشه به جای رشته)؛ رندر مستقیم یه مقدار غیر-رشته‌ای داخل JSX
// ("Objects are not valid as a React child") کل اپ رو کرش می‌ده و باعث
// می‌شه ابزار dev کل صفحه رو reload کنه. پس همیشه قبل از رندر به رشته تبدیلش می‌کنیم.
const toSafeString = (value) => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return "";
};

// اگه به هر دلیلی (مثلاً یه پیام وب‌ساکت با شکل غیرمنتظره) داخل پاپ‌اپ خطا رخ بده،
// این باعندری کل داشبورد رو نمی‌گیره و فقط خود پاپ‌اپ رو مخفی می‌کنه.
class TicketPopupBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    console.error("خطا در نمایش پاپ‌اپ تیکت:", error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * پاپ‌اپ نوتیفیکیشن تیکت جدید — کنار سایدبار می‌شینه و از سمت چپ به راست اسلاید می‌شه.
 * پراپ collapsed رو از AdminSidebar پاس بده تا وقتی سایدبار جمع/باز می‌شه، جای پاپ‌اپ هم درست تنظیم بشه.
 */
function TicketNotificationPopup({ collapsed = false }) {
  const [toasts, setToasts] = useState([]);
  const navigate = useNavigate();
  const lastMessage = useTicketSocket();

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback((rawTicket) => {
    console.log(rawTicket);
    if (!rawTicket || typeof rawTicket !== "object") return;

    // هر فیلد رو ایمن می‌کنیم تا اگه بک‌اند یه شکل غیرمنتظره فرستاد، رندر کرش نکنه
    const ticket = {
      id: rawTicket.ticket_id ?? nextId(),
      subject: toSafeString(rawTicket.title) || "بدون عنوان",
      sender_name: toSafeString(rawTicket.sender_name),
    };

    setToasts((prev) => {
      const next = [{ id: nextId(), ticket, createdAt: Date.now() }, ...prev];
      return next.slice(0, MAX_VISIBLE);
    });
  }, []);

  // ---- گوش دادن به پیام‌های تازه‌ی وب‌ساکت (اتصال واقعی در TicketSocketContext برقرار می‌شه) ----
  useEffect(() => {
    if (!lastMessage) return;
    const data = lastMessage.data;

    // if (data?.type === NEW_TICKET_EVENT && data.ticket) {
    pushToast(data);
    // }
  }, [lastMessage, pushToast]);

  // ---- بستن خودکار هر پاپ‌اپ بعد از مدت معین ----
  useEffect(() => {
    const timers = toasts.map((t) =>
      setTimeout(() => removeToast(t.id), AUTO_DISMISS_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts, removeToast]);

  const handleOpenTicket = (toast) => {
    removeToast(toast.id);
    navigate(`/panel/admin/ticket-archive/${toast.ticket.id}`);
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className={`ticket-popup-stack${collapsed ? " sidebar-collapsed" : ""}`}
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="ticket-popup"
          onClick={() => handleOpenTicket(toast)}
        >
          <div className="ticket-popup-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 8V6a2 2 0 00-2-2H5a2 2 0 00-2 2v2a2 2 0 010 4v2a2 2 0 002 2h14a2 2 0 002-2v-2a2 2 0 010-4z" />
              <path d="M10 8v8" strokeDasharray="1.5 2.2" />
            </svg>
          </div>

          <div className="ticket-popup-body">
            <div className="ticket-popup-headline">
              <span className="ticket-popup-title">تیکت جدید</span>
              <span className="ticket-popup-subject">
                {toast.ticket.subject}
              </span>
            </div>
            {toast.ticket.sender_name && (
              <div className="ticket-popup-sender">
                از طرف: {toast.ticket.sender_name}
              </div>
            )}
          </div>

          <button
            type="button"
            className="ticket-popup-close"
            aria-label="بستن اعلان"
            onClick={(e) => {
              e.stopPropagation();
              removeToast(toast.id);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}

export default function TicketNotificationPopupSafe(props) {
  return (
    <TicketPopupBoundary>
      <TicketNotificationPopup {...props} />
    </TicketPopupBoundary>
  );
}
